import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const token = searchParams.get('token') || '';

  const [valid, setValid] = useState(null);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setValid(false);
        setChecking(false);
        return;
      }
      try {
        const { data } = await authAPI.validateResetToken(token);
        if (!cancelled) setValid(!!data.valid);
      } catch {
        if (!cancelled) setValid(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showError('Şifreler eşleşmiyor');
      return;
    }
    if (password.length < 6) {
      showError('Şifre en az 6 karakter olmalıdır');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.resetPassword({
        token,
        password,
        confirmPassword
      });
      showSuccess(data.message || 'Şifreniz güncellendi.', 5000);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const msg =
        err.response?.data?.error || 'Şifre sıfırlanamadı. Bağlantı süresi dolmuş olabilir.';
      showError(msg, 8000);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex items-center justify-center p-4">
        <p className="text-white text-lg">Bağlantı kontrol ediliyor...</p>
      </div>
    );
  }

  if (!token || valid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex flex-col items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md text-center border border-white/20">
          <p className="text-white mb-6">
            Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş. Yeni bir talep
            oluşturun.
          </p>
          <Link
            to="/forgot-password"
            className="text-blue-200 underline hover:text-white mr-4"
          >
            Şifremi unuttum
          </Link>
          <Link to="/login" className="text-blue-200 underline hover:text-white">
            Giriş
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex flex-col">
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 w-full max-w-md border border-white/20">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">
            Yeni şifre belirle
          </h1>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Yeni şifre
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                autoComplete="off"
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="En az 6 karakter"
              />
            </div>
            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Yeni şifre (tekrar)
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="off"
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-medium disabled:opacity-50"
            >
              {loading ? 'Kaydediliyor...' : 'Şifreyi kaydet'}
            </button>
          </form>
          <p className="text-center mt-6">
            <Link to="/login" className="text-white/90 text-sm underline">
              Giriş sayfasına dön
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
